/**
 * Style Editor Panel
 * Comprehensive CSS property editor with tabs for layout, spacing, typography, background, border, and effects
 */

import React, { useState, useCallback } from 'react';
import {
  Layout,
  Space,
  Type,
  Paintbrush,
  Square,
  Sparkles,
  Code2,
} from 'lucide-react';
import type { BlockStyles } from '../../../types';
import { ColorPicker } from './ColorPicker';
import { SpacingBoxModel } from './SpacingBoxModel';
import { GradientBuilder } from './GradientBuilder';

// ============================================================================
// TYPES
// ============================================================================

interface StyleEditorProps {
  styles: BlockStyles;
  onChange: (styles: BlockStyles) => void;
}

type StyleTab = 'layout' | 'spacing' | 'typography' | 'background' | 'border' | 'effects' | 'custom';

const TABS: { id: StyleTab; label: string; icon: React.ReactNode }[] = [
  { id: 'layout', label: 'Layout', icon: <Layout className="h-3.5 w-3.5" /> },
  { id: 'spacing', label: 'Spacing', icon: <Space className="h-3.5 w-3.5" /> },
  { id: 'typography', label: 'Type', icon: <Type className="h-3.5 w-3.5" /> },
  { id: 'background', label: 'Fill', icon: <Paintbrush className="h-3.5 w-3.5" /> },
  { id: 'border', label: 'Border', icon: <Square className="h-3.5 w-3.5" /> },
  { id: 'effects', label: 'Effects', icon: <Sparkles className="h-3.5 w-3.5" /> },
  { id: 'custom', label: 'CSS', icon: <Code2 className="h-3.5 w-3.5" /> },
];

const FONT_FAMILIES = [
  { value: 'inherit', label: 'Default' },
  { value: 'Inter, sans-serif', label: 'Inter' },
  { value: 'system-ui, sans-serif', label: 'System' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: "'Courier New', monospace", label: 'Courier' },
  { value: "'Playfair Display', serif", label: 'Playfair' },
  { value: "'Roboto', sans-serif", label: 'Roboto' },
];

const FONT_WEIGHTS = [
  { value: '300', label: 'Light' },
  { value: '400', label: 'Regular' },
  { value: '500', label: 'Medium' },
  { value: '600', label: 'Semibold' },
  { value: '700', label: 'Bold' },
  { value: '800', label: 'Extra Bold' },
];

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <label className="block text-xs text-gray-500 mb-1">{children}</label>;
}

function TextInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <input
      type="text"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full border rounded px-2 py-1.5 text-xs"
    />
  );
}

function SelectInput({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border rounded px-2 py-1.5 text-xs"
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function StyleEditor({ styles, onChange }: StyleEditorProps) {
  const [activeTab, setActiveTab] = useState<StyleTab>('spacing');

  const updateStyle = useCallback(
    (key: string, value: string) => {
      onChange({ ...styles, [key]: value || undefined });
    },
    [styles, onChange]
  );

  const s = styles || {};

  return (
    <div className="flex flex-col h-full">
      {/* Tab navigation */}
      <div className="flex border-b overflow-x-auto">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1 px-2.5 py-2 text-xs whitespace-nowrap border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-blue-500 text-blue-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto p-3 space-y-4">
        {/* LAYOUT TAB */}
        {activeTab === 'layout' && (
          <>
            <div>
              <FieldLabel>Display</FieldLabel>
              <SelectInput
                value={s.display || ''}
                onChange={(v) => updateStyle('display', v)}
                options={[
                  { value: '', label: 'Default' },
                  { value: 'block', label: 'Block' },
                  { value: 'flex', label: 'Flex' },
                  { value: 'grid', label: 'Grid' },
                  { value: 'inline-flex', label: 'Inline Flex' },
                  { value: 'none', label: 'Hidden' },
                ]}
              />
            </div>

            {(s.display === 'flex' || s.display === 'inline-flex') && (
              <>
                <div>
                  <FieldLabel>Direction</FieldLabel>
                  <SelectInput
                    value={s.flexDirection || ''}
                    onChange={(v) => updateStyle('flexDirection', v)}
                    options={[
                      { value: 'row', label: 'Row' },
                      { value: 'column', label: 'Column' },
                      { value: 'row-reverse', label: 'Row Reverse' },
                      { value: 'column-reverse', label: 'Column Reverse' },
                    ]}
                  />
                </div>
                <div>
                  <FieldLabel>Justify Content</FieldLabel>
                  <SelectInput
                    value={s.justifyContent || ''}
                    onChange={(v) => updateStyle('justifyContent', v)}
                    options={[
                      { value: 'flex-start', label: 'Start' },
                      { value: 'center', label: 'Center' },
                      { value: 'flex-end', label: 'End' },
                      { value: 'space-between', label: 'Space Between' },
                      { value: 'space-around', label: 'Space Around' },
                      { value: 'space-evenly', label: 'Space Evenly' },
                    ]}
                  />
                </div>
                <div>
                  <FieldLabel>Align Items</FieldLabel>
                  <SelectInput
                    value={s.alignItems || ''}
                    onChange={(v) => updateStyle('alignItems', v)}
                    options={[
                      { value: 'flex-start', label: 'Start' },
                      { value: 'center', label: 'Center' },
                      { value: 'flex-end', label: 'End' },
                      { value: 'stretch', label: 'Stretch' },
                      { value: 'baseline', label: 'Baseline' },
                    ]}
                  />
                </div>
                <div>
                  <FieldLabel>Gap</FieldLabel>
                  <TextInput value={s.gap || ''} onChange={(v) => updateStyle('gap', v)} placeholder="16px" />
                </div>
              </>
            )}

            {s.display === 'grid' && (
              <>
                <div>
                  <FieldLabel>Grid Columns</FieldLabel>
                  <TextInput
                    value={s.gridTemplateColumns || ''}
                    onChange={(v) => updateStyle('gridTemplateColumns', v)}
                    placeholder="repeat(3, 1fr)"
                  />
                </div>
                <div>
                  <FieldLabel>Gap</FieldLabel>
                  <TextInput value={s.gap || ''} onChange={(v) => updateStyle('gap', v)} placeholder="16px" />
                </div>
              </>
            )}

            <div>
              <FieldLabel>Width</FieldLabel>
              <TextInput value={s.width || ''} onChange={(v) => updateStyle('width', v)} placeholder="auto" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <FieldLabel>Min Width</FieldLabel>
                <TextInput value={s.minWidth || ''} onChange={(v) => updateStyle('minWidth', v)} placeholder="0" />
              </div>
              <div>
                <FieldLabel>Max Width</FieldLabel>
                <TextInput value={s.maxWidth || ''} onChange={(v) => updateStyle('maxWidth', v)} placeholder="none" />
              </div>
            </div>
            <div>
              <FieldLabel>Height</FieldLabel>
              <TextInput value={s.height || ''} onChange={(v) => updateStyle('height', v)} placeholder="auto" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <FieldLabel>Min Height</FieldLabel>
                <TextInput value={s.minHeight || ''} onChange={(v) => updateStyle('minHeight', v)} placeholder="0" />
              </div>
              <div>
                <FieldLabel>Max Height</FieldLabel>
                <TextInput value={s.maxHeight || ''} onChange={(v) => updateStyle('maxHeight', v)} placeholder="none" />
              </div>
            </div>
            <div>
              <FieldLabel>Overflow</FieldLabel>
              <SelectInput
                value={s.overflow || ''}
                onChange={(v) => updateStyle('overflow', v)}
                options={[
                  { value: '', label: 'Default' },
                  { value: 'visible', label: 'Visible' },
                  { value: 'hidden', label: 'Hidden' },
                  { value: 'scroll', label: 'Scroll' },
                  { value: 'auto', label: 'Auto' },
                ]}
              />
            </div>
          </>
        )}

        {/* SPACING TAB */}
        {activeTab === 'spacing' && (
          <>
            <SpacingBoxModel
              margin={{
                top: s.marginTop,
                right: s.marginRight,
                bottom: s.marginBottom,
                left: s.marginLeft,
              }}
              padding={{
                top: s.paddingTop,
                right: s.paddingRight,
                bottom: s.paddingBottom,
                left: s.paddingLeft,
              }}
              onMarginChange={(side, value) => {
                const key = `margin${side.charAt(0).toUpperCase()}${side.slice(1)}`;
                updateStyle(key, value);
              }}
              onPaddingChange={(side, value) => {
                const key = `padding${side.charAt(0).toUpperCase()}${side.slice(1)}`;
                updateStyle(key, value);
              }}
            />
            <div>
              <FieldLabel>Padding (shorthand)</FieldLabel>
              <TextInput
                value={s.padding || ''}
                onChange={(v) => updateStyle('padding', v)}
                placeholder="20px"
              />
            </div>
            <div>
              <FieldLabel>Margin (shorthand)</FieldLabel>
              <TextInput
                value={s.margin || ''}
                onChange={(v) => updateStyle('margin', v)}
                placeholder="0"
              />
            </div>
          </>
        )}

        {/* TYPOGRAPHY TAB */}
        {activeTab === 'typography' && (
          <>
            <div>
              <FieldLabel>Font Family</FieldLabel>
              <SelectInput
                value={s.fontFamily || ''}
                onChange={(v) => updateStyle('fontFamily', v)}
                options={FONT_FAMILIES}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <FieldLabel>Font Size</FieldLabel>
                <TextInput
                  value={s.fontSize || ''}
                  onChange={(v) => updateStyle('fontSize', v)}
                  placeholder="16px"
                />
              </div>
              <div>
                <FieldLabel>Font Weight</FieldLabel>
                <SelectInput
                  value={s.fontWeight || ''}
                  onChange={(v) => updateStyle('fontWeight', v)}
                  options={[{ value: '', label: 'Default' }, ...FONT_WEIGHTS]}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <FieldLabel>Line Height</FieldLabel>
                <TextInput
                  value={s.lineHeight || ''}
                  onChange={(v) => updateStyle('lineHeight', v)}
                  placeholder="1.5"
                />
              </div>
              <div>
                <FieldLabel>Letter Spacing</FieldLabel>
                <TextInput
                  value={s.letterSpacing || ''}
                  onChange={(v) => updateStyle('letterSpacing', v)}
                  placeholder="normal"
                />
              </div>
            </div>
            <div>
              <ColorPicker
                value={s.color || s.textColor || ''}
                onChange={(v) => updateStyle('color', v)}
                label="Text Color"
              />
            </div>
            <div>
              <FieldLabel>Text Align</FieldLabel>
              <div className="flex gap-1">
                {(['left', 'center', 'right', 'justify'] as const).map((align) => (
                  <button
                    key={align}
                    type="button"
                    onClick={() => updateStyle('textAlign', align)}
                    className={`flex-1 py-1.5 text-xs border rounded capitalize transition-colors ${
                      s.textAlign === align ? 'bg-blue-50 border-blue-300 text-blue-700' : 'hover:bg-gray-50'
                    }`}
                  >
                    {align}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <FieldLabel>Text Decoration</FieldLabel>
              <SelectInput
                value={s.textDecoration || ''}
                onChange={(v) => updateStyle('textDecoration', v)}
                options={[
                  { value: '', label: 'None' },
                  { value: 'underline', label: 'Underline' },
                  { value: 'line-through', label: 'Line Through' },
                  { value: 'overline', label: 'Overline' },
                ]}
              />
            </div>
          </>
        )}

        {/* BACKGROUND TAB */}
        {activeTab === 'background' && (
          <>
            <div>
              <ColorPicker
                value={s.backgroundColor || ''}
                onChange={(v) => updateStyle('backgroundColor', v)}
                label="Background Color"
              />
            </div>
            <div>
              <FieldLabel>Background Image</FieldLabel>
              <TextInput
                value={s.backgroundImage || ''}
                onChange={(v) => updateStyle('backgroundImage', v)}
                placeholder="url(...)"
              />
            </div>
            <div>
              <FieldLabel>Background Gradient</FieldLabel>
              <GradientBuilder
                value={s.backgroundGradient || ''}
                onChange={(v) => updateStyle('backgroundGradient', v)}
              />
            </div>
            <div>
              <FieldLabel>Background Size</FieldLabel>
              <SelectInput
                value={s.backgroundSize || ''}
                onChange={(v) => updateStyle('backgroundSize', v)}
                options={[
                  { value: '', label: 'Default' },
                  { value: 'cover', label: 'Cover' },
                  { value: 'contain', label: 'Contain' },
                  { value: 'auto', label: 'Auto' },
                ]}
              />
            </div>
            <div>
              <FieldLabel>Background Position</FieldLabel>
              <SelectInput
                value={s.backgroundPosition || ''}
                onChange={(v) => updateStyle('backgroundPosition', v)}
                options={[
                  { value: '', label: 'Default' },
                  { value: 'center', label: 'Center' },
                  { value: 'top', label: 'Top' },
                  { value: 'bottom', label: 'Bottom' },
                  { value: 'left', label: 'Left' },
                  { value: 'right', label: 'Right' },
                ]}
              />
            </div>
          </>
        )}

        {/* BORDER TAB */}
        {activeTab === 'border' && (
          <>
            <div>
              <FieldLabel>Border Width</FieldLabel>
              <TextInput
                value={s.borderWidth || ''}
                onChange={(v) => updateStyle('borderWidth', v)}
                placeholder="0px"
              />
            </div>
            <div>
              <FieldLabel>Border Style</FieldLabel>
              <SelectInput
                value={s.borderStyle || ''}
                onChange={(v) => updateStyle('borderStyle', v)}
                options={[
                  { value: '', label: 'None' },
                  { value: 'solid', label: 'Solid' },
                  { value: 'dashed', label: 'Dashed' },
                  { value: 'dotted', label: 'Dotted' },
                  { value: 'double', label: 'Double' },
                ]}
              />
            </div>
            <div>
              <ColorPicker
                value={s.borderColor || ''}
                onChange={(v) => updateStyle('borderColor', v)}
                label="Border Color"
              />
            </div>
            <div>
              <FieldLabel>Border Radius</FieldLabel>
              <TextInput
                value={s.borderRadius || ''}
                onChange={(v) => updateStyle('borderRadius', v)}
                placeholder="0px"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <FieldLabel>Top Left</FieldLabel>
                <TextInput
                  value={s.borderTopLeftRadius || ''}
                  onChange={(v) => updateStyle('borderTopLeftRadius', v)}
                  placeholder="0"
                />
              </div>
              <div>
                <FieldLabel>Top Right</FieldLabel>
                <TextInput
                  value={s.borderTopRightRadius || ''}
                  onChange={(v) => updateStyle('borderTopRightRadius', v)}
                  placeholder="0"
                />
              </div>
              <div>
                <FieldLabel>Bottom Left</FieldLabel>
                <TextInput
                  value={s.borderBottomLeftRadius || ''}
                  onChange={(v) => updateStyle('borderBottomLeftRadius', v)}
                  placeholder="0"
                />
              </div>
              <div>
                <FieldLabel>Bottom Right</FieldLabel>
                <TextInput
                  value={s.borderBottomRightRadius || ''}
                  onChange={(v) => updateStyle('borderBottomRightRadius', v)}
                  placeholder="0"
                />
              </div>
            </div>
          </>
        )}

        {/* EFFECTS TAB */}
        {activeTab === 'effects' && (
          <>
            <div>
              <FieldLabel>Box Shadow</FieldLabel>
              <TextInput
                value={s.boxShadow || ''}
                onChange={(v) => updateStyle('boxShadow', v)}
                placeholder="0 4px 6px rgba(0,0,0,0.1)"
              />
              <div className="flex flex-wrap gap-1 mt-1.5">
                {[
                  { label: 'None', value: 'none' },
                  { label: 'SM', value: '0 1px 2px rgba(0,0,0,0.05)' },
                  { label: 'MD', value: '0 4px 6px -1px rgba(0,0,0,0.1)' },
                  { label: 'LG', value: '0 10px 15px -3px rgba(0,0,0,0.1)' },
                  { label: 'XL', value: '0 20px 25px -5px rgba(0,0,0,0.1)' },
                ].map((preset) => (
                  <button
                    key={preset.label}
                    type="button"
                    onClick={() => updateStyle('boxShadow', preset.value)}
                    className={`px-2 py-0.5 text-[10px] border rounded transition-colors ${
                      s.boxShadow === preset.value
                        ? 'bg-blue-50 border-blue-300 text-blue-700'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <FieldLabel>Opacity</FieldLabel>
              <div className="flex items-center gap-2">
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={s.opacity || '1'}
                  onChange={(e) => updateStyle('opacity', e.target.value)}
                  className="flex-1"
                />
                <span className="text-xs text-gray-600 w-8 text-right">
                  {Math.round(parseFloat(s.opacity || '1') * 100)}%
                </span>
              </div>
            </div>
            <div>
              <FieldLabel>Transform</FieldLabel>
              <TextInput
                value={s.transform || ''}
                onChange={(v) => updateStyle('transform', v)}
                placeholder="rotate(0deg) scale(1)"
              />
            </div>
            <div>
              <FieldLabel>Transition</FieldLabel>
              <TextInput
                value={s.transition || ''}
                onChange={(v) => updateStyle('transition', v)}
                placeholder="all 0.3s ease"
              />
            </div>
          </>
        )}

        {/* CUSTOM CSS TAB */}
        {activeTab === 'custom' && (
          <div>
            <FieldLabel>Custom CSS</FieldLabel>
            <textarea
              value={s.customCss || ''}
              onChange={(e) => updateStyle('customCss', e.target.value)}
              placeholder={`/* CSS properties */\nbackground: linear-gradient(...);\nfilter: blur(4px);`}
              className="w-full border rounded px-3 py-2 text-xs font-mono h-48 resize-none"
              spellCheck={false}
            />
            <p className="text-[10px] text-gray-400 mt-1">
              Enter raw CSS properties. These override other style settings.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
